import type { ZodSchema } from 'zod';
import type { ComponentType } from 'react';
import type { Sport } from '@/types/sports.model';
import type {
  AmericanFinalBracketSize,
  AmericanGroupFormat,
  Category,
  CategoryRankingTarget,
  Club,
  Gender,
  PaymentType,
  Price,
  TournamentType,
  FixtureType,
  TournamentFormat,
  FormSchedule,
  OverflowGroupClassification,
} from '@/types/tournament.model';
import type { WeeklyScheduleSlot } from '@/types/tournament-round.model';
import type { WeeklyGridConfig } from '@/types/schedule.model';

// ============================================================================
// STEP CONFIGURATION
// ============================================================================

/**
 * Configuration for a single wizard step.
 * Steps are defined declaratively and resolved at runtime.
 */
export interface WizardStepConfig {
  /** Unique identifier for the step */
  id: string;

  /** Display title shown in the stepper */
  title: string;

  /** Optional description for the step */
  description?: string;

  /** Key to resolve the component from the Step Registry */
  componentKey: string;

  /**
   * Zod schema for validating the step's data slice.
   * If provided, the engine uses this to determine if the step is complete.
   */
  validationSchema?: ZodSchema;

  /**
   * Conditional visibility function.
   * If returns false, the step is skipped in the flow.
   * @param draft - Current tournament draft state
   */
  isVisible?: (draft: TournamentDraft) => boolean;

  /**
   * Step IDs that must be completed before this step can be accessed.
   * Used for validation and navigation guards.
   */
  dependsOn?: string[];

  /**
   * The key in TournamentDraft where this step stores its data.
   * Used by the engine to read/write step-specific data.
   * Optional for read-only steps like Summary that don't store their own data.
   */
  draftSliceKey?: keyof TournamentDraftSlices;
}

// ============================================================================
// FLOW CONFIGURATION
// ============================================================================

/**
 * Configuration for a complete wizard flow.
 * Each sport can have its own flow configuration.
 */
export interface WizardFlowConfig {
  /** Unique identifier for the flow (e.g., 'padel-default', 'tennis-default') */
  id: string;

  /** Sport UID this flow belongs to. Null for the initial sport selection flow. */
  sportId: string | null;

  /** Display name for the flow */
  name: string;

  /** Ordered list of steps in this flow */
  steps: WizardStepConfig[];
}

// ============================================================================
// DRAFT SLICES - Data structures for each step
// ============================================================================

export interface SportSlice {
  sport: Sport;
}

export interface BasicInfoSlice {
  name: string;
  description: string;
  prizes: string;
  bannerUrl: string;
}

export interface TeamConfigSlice {
  gender: Gender;
  playersPerTeam: number;
  minCategory: Category;
  maxCategory: Category;
  forceSum: boolean;
  /** When true, `minAge` is sent; when false, 0 is sent (no minimum restriction). */
  minAgeRestrictionEnabled?: boolean;
  /** When true, `maxAge` is sent; when false, 200 is sent (no maximum restriction). */
  maxAgeRestrictionEnabled?: boolean;
  /** Minimum age when the minimum-age restriction is enabled. */
  minAge?: number;
  /** Maximum age when the maximum-age restriction is enabled. */
  maxAge?: number;
}

export interface FixtureConfigSlice {
  fixtureType: FixtureType;
  /** SHORT | AMERICAN | LONG — API field `format` (not the same as fixture structure). */
  format: TournamentFormat;
  teamsPerGroups: number;
  classifiedPerGroups: number;
  minTeamsAllowed: number;
  maxTeamsAllowed: number;
  /**
   * Short + groups: occasional groups of 4 — 3 vs 2 classified teams.
   * {@link OverflowGroupClassification.EXTRA_CLASSIFIER} | {@link OverflowGroupClassification.STANDARD}.
   */
  overflowGroupClassification?: OverflowGroupClassification;
  /** Final knockout bracket size for AMERICAN format tournaments. */
  americanFinalBracketSize?: AmericanFinalBracketSize;
  /** Group format for AMERICAN tournaments. Default: WINNER_LOSER. */
  americanGroupFormat?: AmericanGroupFormat;
}

export interface PricingSlice {
  price: Price;
  paymentType: PaymentType;
  type: TournamentType; // STRICT or SOFT (approval required)
  isValid?: boolean; // Price validation status (price > fixedFeeAmount)
}

export interface ClubSelectionSlice {
  selectedClub: Club | null;
  clubs: Club[];
}

export interface ScheduleSlice {
  startDate: Date | null;
  endDate: Date | null;
  signupDeadlineDate: Date | null;
  matchDuration: number;
  schedule: FormSchedule[];
}

/**
 * Tournament Configuration slice - global tournament settings.
 * Contains settings that apply to the entire tournament.
 */
export interface TournamentConfigSlice {
  /** Registration closing date */
  signupDeadlineDate: Date | null;

  /** Match duration in minutes (30-120) */
  matchDuration: number;

  /**
   * Minimum number of individual time-slots a player must select
   * when registering for this tournament.
   * null / undefined means no override → backend defaults to 1.
   */
  minSchedulesRequired: number | null;

  /** Whether the tournament awards points to the club's category ranking. Default true. */
  countsForCategoryRanking: boolean;

  /** Where the points go. Only applies when countsForCategoryRanking is true. */
  categoryRankingTarget: CategoryRankingTarget;

  /** Target category when categoryRankingTarget is FIXED_CATEGORY. */
  categoryRankingCategory: Category | null;

  /** Validation flag for step completion */
  isValid?: boolean;

  // Weekly configuration fields for LONG tournaments (all optional)
  // minSchedulesRequired is already declared above (shared with the other formats)
  availabilityDeadlineDay?: string | null;
  availabilityDeadlineTime?: string | null;
  walkoversForDisqualification?: number | null;
  autoPublishRounds?: boolean;
}

/**
 * Club-level schedule selection for multi-club tournaments.
 * Groups selected schedules by their parent club.
 */
export interface ClubScheduleSelection {
  /** Club ID */
  clubId: string;
  /** Full club object for display and API submission */
  club: Club;
  /** Selected schedule IDs for this club */
  scheduleIds: string[];
}

/**
 * Club Schedules slice - multi-club schedule selection.
 * Schedule selection is grouped by club to support multi-venue tournaments.
 */
export interface ClubSchedulesSlice {
  /**
   * Multi-club schedule selections.
   * Each entry contains a club and its selected schedules.
   */
  clubSchedules: ClubScheduleSelection[];

  /**
   * Match duration (minutes) for which current selections are valid.
   * When tournamentConfig.matchDuration changes, step is reset if different.
   */
  validForMatchDuration?: number | null;

  /**
   * Rows of the chosen LONG weekly grid, kept only to render the summary.
   * The backend receives the grid id (schedule: [{ id }]), not this list.
   */
  weeklyScheduleTemplate?: WeeklyScheduleSlot[];

  /** LONG: first-round start date ('yyyy-MM-dd'). Defines the tournament startDate. */
  weekStartDate?: string;

  /** LONG: weekly grid chosen or created for the tournament. */
  weeklyGridId?: string;

  /** LONG: name of that grid, for the summary. */
  weeklyGridName?: string;

  /** LONG: settings adopted from the tournaments already using the grid; null when nobody uses it. */
  weeklyGridConfig?: WeeklyGridConfig | null;
}

/**
 * @deprecated Use TournamentConfigSlice and ClubSchedulesSlice instead.
 * Kept for backward compatibility during migration.
 */
export interface ScheduleSelectionSlice {
  clubSchedules: ClubScheduleSelection[];
  signupDeadlineDate: Date | null;
  matchDuration: number;
}

/**
 * Union type of all draft slices for type-safe access.
 */
export interface TournamentDraftSlices {
  sport: SportSlice | null;
  basicInfo: BasicInfoSlice | null;
  teamConfig: TeamConfigSlice | null;
  fixtureConfig: FixtureConfigSlice | null;
  pricing: PricingSlice | null;
  clubSelection: ClubSelectionSlice | null;
  schedule: ScheduleSlice | null;
  scheduleSelection: ScheduleSelectionSlice | null;
  tournamentConfig: TournamentConfigSlice | null;
  clubSchedules: ClubSchedulesSlice | null;
}

// ============================================================================
// TOURNAMENT DRAFT - Central state
// ============================================================================

/**
 * The complete tournament draft state.
 * Persisted to localStorage and managed by the wizard store.
 */
export interface TournamentDraft extends TournamentDraftSlices {
  /** Unique identifier for this draft (used in URL: SPORT-XXXXX-XXXX) */
  id: string;

  /** ISO timestamp when the draft was created */
  createdAt: string;

  /** ISO timestamp when the draft was last updated */
  updatedAt: string;

  /** Current step ID the user is on */
  currentStepId: string;

  /** Flow ID being used (changes when sport is selected) */
  flowId: string;

  /** Set of completed step IDs */
  completedSteps: string[];
}

// ============================================================================
// STEP COMPONENT PROPS
// ============================================================================

/**
 * Props passed to every step component by the wizard engine.
 * Steps should NOT know about navigation - they only read/write their slice.
 */
export interface WizardStepProps<T = unknown> {
  /** The current data for this step's slice */
  data: T | null;

  /** Function to update this step's data slice */
  updateData: (data: Partial<T>) => void;

  /** The complete draft (read-only, for conditional rendering) */
  draft: TournamentDraft;

  /** Whether the step is in a loading state */
  isLoading?: boolean;

  /** Validation errors for the current step */
  errors?: Record<string, string>;
}

/**
 * Type for step components in the registry.
 */
export type WizardStepComponent<T = unknown> = ComponentType<WizardStepProps<T>>;

// ============================================================================
// WIZARD ENGINE STATE
// ============================================================================

/**
 * Navigation direction for analytics and animations.
 */
export type NavigationDirection = 'forward' | 'backward' | 'jump';

/**
 * Result of a navigation attempt.
 */
export interface NavigationResult {
  success: boolean;
  error?: string;
  targetStepId?: string;
}

/**
 * The wizard engine's public API.
 * Exposed via context to all wizard components.
 */
export interface WizardEngine {
  // Current state
  draft: TournamentDraft;
  flowConfig: WizardFlowConfig;
  currentStep: WizardStepConfig;
  currentStepIndex: number;
  visibleSteps: WizardStepConfig[];

  // Navigation state
  canGoNext: boolean;
  canGoBack: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSummaryStep: boolean;

  // Navigation actions
  goNext: () => NavigationResult;
  goBack: () => NavigationResult;
  goTo: (stepId: string) => NavigationResult;

  // Draft management
  updateStepData: <K extends keyof TournamentDraftSlices>(
    key: K,
    data: Partial<NonNullable<TournamentDraftSlices[K]>>
  ) => void;
  setStepData: <K extends keyof TournamentDraftSlices>(
    key: K,
    data: TournamentDraftSlices[K]
  ) => void;

  // Step validation
  isStepComplete: (stepId: string) => boolean;
  isStepAccessible: (stepId: string) => boolean;
  validateCurrentStep: () => boolean;

  // Flow management
  changeFlow: (flowId: string, preserveSportSlice?: boolean) => void;

  // Lifecycle
  reset: () => void;
  submit: () => Promise<void>;
  isSubmitting: boolean;
}

// ============================================================================
// STEP REGISTRY TYPES
// ============================================================================

/**
 * Entry in the step registry.
 */
export interface StepRegistryEntry {
  /** The step component */
  component: WizardStepComponent;

  /** Default validation schema (can be overridden in flow config) */
  defaultSchema?: ZodSchema;
}

/**
 * The step registry maps component keys to their implementations.
 */
export type StepRegistry = Record<string, StepRegistryEntry>;

// ============================================================================
// URL PATH UTILITIES
// ============================================================================

/**
 * Format for step URLs: /crear-torneo/{sport}-{stepId}
 * Examples:
 *   - /crear-torneo/padel-basic-info
 *   - /crear-torneo/tennis-pricing
 *   - /crear-torneo/squash-schedule
 * 
 * Special case for sport selection (before sport is chosen):
 *   - /crear-torneo/select-sport
 */
export interface StepUrlComponents {
  /** Sport ID in lowercase (e.g., 'padel', 'tennis') */
  sportId: string | null;

  /** Step ID (e.g., 'basic-info', 'pricing') */
  stepId: string;
}

/**
 * Builds the URL path for a step.
 */
export function buildStepPath(sportId: string | null, stepId: string): string {
  if (!sportId) {
    return `select-${stepId}`;
  }
  return `${sportId.toLowerCase()}-${stepId}`;
}

/**
 * Parses a step path into its components.
 */
export function parseStepPath(path: string): StepUrlComponents {
  // Handle special case for sport selection
  if (path.startsWith('select-')) {
    return {
      sportId: null,
      stepId: path.replace('select-', ''),
    };
  }

  // Find the first hyphen that separates sport from step
  const firstHyphenIndex = path.indexOf('-');
  if (firstHyphenIndex === -1) {
    return { sportId: null, stepId: path };
  }

  return {
    sportId: path.substring(0, firstHyphenIndex),
    stepId: path.substring(firstHyphenIndex + 1),
  };
}

// ============================================================================
// PERSISTENCE TYPES
// ============================================================================

/**
 * Structure stored in localStorage.
 * Only ONE draft is allowed at a time.
 */
export interface PersistedWizardState {
  /** The single active draft (null if no draft exists) */
  draft: TournamentDraft | null;

  /** Timestamp of last update for staleness checks */
  lastUpdated: string | null;
}

/**
 * Options for draft persistence.
 */
export interface PersistenceOptions {
  /** localStorage key */
  storageKey: string;

  /** Auto-save debounce delay in ms */
  autoSaveDelay: number;
}
